import React, { useContext, useMemo, useState } from "react";
import Select from "common/Select/Select";
import { checkSearch, checkSymbolSearch } from "@shared/utils/misc";
import { ErrorText } from "features/bots/common/styles";
import { useTheme } from "styled-components";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types/common/common.types";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import { openMessage } from "utils/misc";
import { UseSymbolAndAccountSelectorsProps } from "@shared/features/bots/BotForm/common/types";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup";
import CoinsIconsGroup from "features/portfolio/CoinsIconsGroup";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { BotType } from "@shared/types";
import MultiSymbolSelect from "features/bots/BotForm/General/MultiSymbolSelect";
import useSharedSymbolAndAccountSelectors from "@shared/features/bots/BotForm/useSharedSymbolAndAccountSelectors";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { useClientsData } from "@shared/utils/hooks/misc";
import {
  BOT_FORM_ACCOUNT_ERROR_ID,
  BOT_FORM_ACCOUNT_SELECT_ID,
  BOT_FORM_SYMBOL_SELECT_ID,
} from "testIds.ts";

type SymbolOption = {
  label: React.JSX.Element;
  value: string;
};

const useSymbolAndAccountSelectors = ({
  defaultExchange,
  defaultAccountId,
  defaultSymbols,
  defaultBlacklistSymbols,
  initialize,
  disable,
  symbols,
  setSymbols,
  account,
  setAccount,
  isTrade,
  onSymbolChanged,
  onAccountChanged,
  useFreeText,
  onChangeWithWarning,
}: UseSymbolAndAccountSelectorsProps) => {
  const { botType, isChannelConfiguration } = useContext(BotFormContext) || {};
  const appTheme = useTheme();
  const { getAccountById } = useClientsData();
  const [noAccountModalOpen, setNoAccountModalOpen] = useState(false);

  const {
    symbolErrorMessage,
    symbolIsValid,
    accountErrorMessage,
    symbolErrorMessageHelperData,
    accountIsValid,
    accountSelectorData,
    symbolSelectorData,
    getExchangeErrorMessage,
    getAccountErrorMessage,
  } = useSharedSymbolAndAccountSelectors({
    defaultExchange,
    defaultAccountId,
    defaultSymbols,
    defaultBlacklistSymbols,
    initialize,
    openMessage,
    symbols,
    setSymbols,
    account,
    setAccount,
    isTrade,
    onSymbolChanged,
    onAccountChanged,
    useFreeText,
    onChangeWithWarning,
  });
  const isSignalsBot = botType === BotType.SIGNAL;
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const symbolError = symbolErrorMessage ? (
    <ErrorText
      style={symbolIsValid ? { color: appTheme.colors.gold } : {}}
      as="div"
    >
      {symbolErrorMessage}
      {!!symbolErrorMessageHelperData && (
        <HelperTooltip {...symbolErrorMessageHelperData} />
      )}
    </ErrorText>
  ) : undefined;

  const accountError = accountErrorMessage ? (
    <ErrorText
      data-testid={BOT_FORM_ACCOUNT_ERROR_ID}
      style={accountIsValid ? { color: appTheme.colors.gold } : {}}
    >
      {accountErrorMessage}
    </ErrorText>
  ) : undefined;

  const getAccountPlaceholder = () => {
    if (accountSelectorData.exchanges.length) {
      return (
        <ExchangesIconsGroup
          exchanges={accountSelectorData.exchanges}
          size={24}
          style={{ marginLeft: 8, marginTop: 3 }}
        />
      );
    } else if (!accountSelectorData.value) {
      return t("placeholders.choose_account");
    }
  };

  const onAccountSelectorClick = () => {
    if (accountSelectorData.showEmptyState) {
      setNoAccountModalOpen(true);
    }
  };

  const parsedOptions = accountSelectorData.options?.map((accountData) => {
    const label = (
      <TextWithIcon
        coinOrExchangeName={accountData.exchange}
        text={accountData.client_name}
        iconType={ICON_TYPES.EXCHANGE}
        style={{ opacity: accountData.isInvalid ? 0.4 : 1 }}
      />
    );
    return {
      value: accountData.client_id.toString(),
      label,
    };
  });

  const accountSelector =
    isChannelConfiguration ||
    botType === BotType.TRADING_VIEW_SIGNALS ? undefined : (
      <Select
        data-testid={BOT_FORM_ACCOUNT_SELECT_ID}
        open={accountSelectorData.showEmptyState ? false : undefined}
        style={{ width: "100%" }}
        value={accountSelectorData.value}
        onChange={accountSelectorData.onChange}
        popupMatchSelectWidth={false}
        optionLabelProp="label"
        disabled={disable}
        showSearch={
          !accountSelectorData.showEmptyState
            ? {
                filterOption: (searchValue, option) =>
                  checkSearch(
                    getAccountById(Number(option?.value))?.client_name,
                    searchValue
                  ),
              }
            : false
        }
        placeholder={getAccountPlaceholder()}
        mode={accountSelectorData.isMultiSelect ? "tags" : undefined}
        onClick={onAccountSelectorClick}
        options={parsedOptions}
        mobileTitle={accountSelectorData.title}
      />
    );

  const symbolOptions = useMemo<SymbolOption[]>(() => {
    return symbolSelectorData.options.map((symbol) => {
      return {
        label: (
          <span data-testid={`${BOT_FORM_SYMBOL_SELECT_ID}-${symbol}`}>
            <TextWithIcon
              coinOrExchangeName={symbol}
              text={symbol}
              iconType={ICON_TYPES.COIN}
            />
          </span>
        ),
        value: symbol,
      };
    });
  }, [symbolSelectorData.options]);

  const getSymbolsPlaceholder = () => {
    if (symbolSelectorData.coins.length) {
      return (
        <CoinsIconsGroup
          coins={symbolSelectorData.coins}
          size={24}
          style={{ marginLeft: 8 }}
          maxCount={3}
        />
      );
    }
    return t("placeholders.choose_symbol");
  };

  const getSymbolsSelector = () => {
    if (isSignalsBot) return <MultiSymbolSelect />;
    return (
      <Select<string, SymbolOption>
        data-testid={BOT_FORM_SYMBOL_SELECT_ID}
        placeholder={getSymbolsPlaceholder()}
        onChange={(symbol) => symbolSelectorData.onChange(symbol)}
        value={symbolSelectorData.value}
        optionLabelProp="label"
        style={{
          width: "100%",
        }}
        options={symbolOptions}
        showSearch={{
          filterOption: (searchValue?: string, option?: SymbolOption) =>
            checkSymbolSearch(option?.value, searchValue),
        }}
        error={!!symbolError}
        disabled={disable}
        mobileTitle={symbolSelectorData.title}
      />
    );
  };

  const closeNoAccountModal = () => setNoAccountModalOpen(false);

  return {
    symbolSelector: getSymbolsSelector(),
    accountSelector,
    symbolError,
    accountError,
    accountIsValid,
    symbolIsValid,
    accountSelectorTitle: accountSelectorData.title,
    symbolSelectorTitle: symbolSelectorData.title,
    noAccountModalOpen,
    closeNoAccountModal,
    getAccountErrorMessage,
    getExchangeErrorMessage,
  };
};

export default useSymbolAndAccountSelectors;
